/**
 * Visibility gate for the Interactive OE Report nav link, backed by the SSM
 * StringList `/das-finsuite/fe/oe-report-allowed-aliases-{stage}`.
 *
 * Edited out-of-band (CLI/console), not via CDK, so the preview audience changes
 * without a deploy. Fails closed. Hides the nav link only — the route stays
 * reachable by URL, so this is not a security boundary.
 */

import { GetParameterCommand, SSMClient } from '@aws-sdk/client-ssm';

import { ENVIRONMENT_VARIABLES, NORMALIZED_STAGE } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';
import { logger } from 'src/logger';

const PARAM_NAME = `/das-finsuite/fe/oe-report-allowed-aliases-${NORMALIZED_STAGE}`;
const CACHE_TTL_MS = 60_000;

let ssmClient: SSMClient | null = null;
let cachedAliases: { value: string[]; expiresAt: number } | null = null;
let inFlight: Promise<string[]> | null = null;

/**
 * Cached for the page's lifetime. Safe across token rotation because
 * `CognitoService.credentialProvider` is an async provider function, which the SDK
 * re-invokes per request rather than reading a captured credential object.
 */
const getClient = (): SSMClient => {
  if (ssmClient) return ssmClient;
  ssmClient = new SSMClient({
    region: ENVIRONMENT_VARIABLES.env.Region,
    credentials: CognitoService.credentialProvider
  });
  return ssmClient;
};

const fetchFresh = async (): Promise<string[]> => {
  try {
    const client = getClient();
    const response = await client.send(new GetParameterCommand({ Name: PARAM_NAME }));
    return (response.Parameter?.Value ?? '')
      .split(',')
      .map((a) => a.trim().toLowerCase())
      .filter(Boolean);
  } catch (err) {
    logger.warn?.('OE report allowlist fetch failed; hiding nav link', {
      param: PARAM_NAME,
      error: err instanceof Error ? err.message : String(err)
    });
    return [];
  }
};

/** Allowed aliases for this stage. Coalesces concurrent calls; cached ~60s. */
export const getOEReportAllowedAliases = async (): Promise<string[]> => {
  const now = Date.now();
  if (cachedAliases && cachedAliases.expiresAt > now) {
    return cachedAliases.value;
  }
  if (inFlight) return inFlight;

  inFlight = fetchFresh()
    .then((value) => {
      cachedAliases = { value, expiresAt: Date.now() + CACHE_TTL_MS };
      return value;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

/** Whether the given alias may see the Interactive OE Report nav link. */
export const canSeeOEReportLink = async (alias: string | undefined): Promise<boolean> => {
  if (!alias) return false;
  const allowed = await getOEReportAllowedAliases();
  return allowed.includes(alias.trim().toLowerCase());
};

/** Test-only: clear the module-level cache and client. */
export const __resetOEReportAccessCache = (): void => {
  cachedAliases = null;
  inFlight = null;
  ssmClient = null;
};
